import { AnyID, LinkedItem, Morphs } from '@types'
import { sceneGLBLookupKey } from '@context/utils/linked/buildLinkedCatalogId'
import { isKbrRinconeroFamilyCatalog, applyKbrLegacyRegletaPlus20 } from '@utils/catalog/kbrCatalog'
import { API } from '@utils/queryBuilder'
import getMorphsFromGLB from '../../../store/getMorphsFromGLB'
import getId from '@utils/sceneObjects/getId'
import { getGLB } from '@utils/sceneObjects/items/getGLB'
import { getGLBSize, getGLBSizeForFreeDimension, getCatalogGlbAabbMm, getObjectLocalAabbMm } from '@utils/sceneObjects/items/getGLBSize'
import { newGetDataFromGLB } from '@utils/sceneObjects/items/newGetDataFromGLB'
import { setGLTFLoader } from '@utils/sceneObjects/setGLTFLoader'
import {
  beginSceneCostSession,
  endSceneCostSession,
  markSceneCostPhase,
} from '@utils/debug/sceneCostProfiler'
import { isRinconeroCatalog } from '@utils/sceneObjects/snappedSides'
import {
  activateSyntheticEncimeraFootprintMorphs,
  applyFreeDimensionUniversalStyleMorphs,
  applyLinkedApplianceLocalTransformFromApi,
  ensureFreeDimensionCatalogBases,
  getFreeDimensionAltoOffsetMm,
  getFreeDimensionPlacementStoreAltoMm,
  getLinkedPlacaCenteredLocalXMm,
  getRinconeroIndependentZocaloLayout,
  INDEPENDENT_ZOCALO_CATALOG_ID,
  INDEPENDENT_ZOCALO_HEIGHT_MM,
  INDEPENDENT_ZOCALO_RETURN_OPTION,
  independentZocaloSceneGlbKeys,
  isEncimeraLinkedSonItem,
  isFreeDimensionAnchoApiMorphCatalog,
  isFreeDimensionLateralOrCostadoCatalog,
  isFreeDimensionTpcNoMorphCatalog,
  isIndependentZocaloLinkedSon,
  isMalFreeDimensionUnit,
  isRb805Catalog,
  isRbFreeDimensionRegleta,
  itemUsesFreeDimensionAltoApiMorph,
  isPlacaLinkedApplianceSon,
  linkedSonKeepsApiLocalTransform,
  matchesRbRegletaPreferredEncimera,
  placeLinkedEncimeraY,
  placeLinkedIndependentZocalo,
  raiseParentForIndependentZocalo,
  RB_REGLETA_ENCIMERA_CODIGO,
  resolveEncimeraAcabadoAjusteYMm,
  resolveFreeDimensionAltoViaGlbMorph,
  resolveFreeDimensionAnchoViaGlbMorph,
  resolveFreeDimensionFondoViaGlbMorph,
  resolveFreeDimensionScaleBaseMm,
  revertIndependentZocaloFloorOffsetIfNeeded,
  sealLinkedApplianceApiFloorCoordsFromParentCatalog,
  shouldUseIndependentZocaloUnit,
  usesFreeDimensionScaling,
  usesPureGroupFreeDimensionScale,
  vector3ToFreeDimensionGlbBaseMm,
} from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'
import { mergeGlbZoneTextureWithUserApplied } from '@utils/sceneObjects/linked/reconcileTexturesAfterUnitChange'
import { syncLinkedItemZonesFromTextures } from '@context/utils/linked/syncLinkedItemZonesFromTextures'
import { Matrix4 } from 'three'

type StoreSlice = {
  items: Map<AnyID, any>
  sceneGLB: Map<string | number, { glb: any; backGLBInfo: any }>
  updateLinkedSonPositions?: (id: string, morphTarget: 'Ancho' | 'Fondo' | 'Alto') => void
  syncLateralsAfterFurnitureDimensionChange?: (furnitureId: AnyID) => number
}

type MigrateDeps = {
  get: () => StoreSlice
  set: (fn: (store: StoreSlice) => void) => void
  /** Si se pasa, solo migra esos padres (p.ej. items recién añadidos). */
  onlyParentIds?: Iterable<string>
  /**
   * Unidades del open-project (`backResponse.unidades`).
   * Por defecto NO se usan: las medidas vienen del JSON.
   * Solo excepciones (`UNIDADES_DIMS_JSON_OVERRIDE_EXCEPTIONS`).
   */
  projectUnidades?: ProjectUnidadDims[] | null
  /** idVersiones del proyecto abierto (para excepciones JSON incorrecto). */
  idVersiones?: number | null
}

/** Dimensiones de catálogo que vienen con el proyecto abierto. */
export type ProjectUnidadDims = {
  idUnidades?: number
  codigo?: string
  referencia?: string
  ancho?: number
  fondo?: number
  alto?: number
  altura?: number | null
  tipoNormalizado?: string
}

/**
 * Casos puntuales: el JSON de escena trae medidas incorrectas → usar `unidades`.
 * Resto de proyectos/unidades: siempre JSON.
 */
export const UNIDADES_DIMS_JSON_OVERRIDE_EXCEPTIONS: ReadonlyArray<{
  idVersiones: number
  catalogCodes: readonly string[]
  idUnidades: readonly number[]
}> = [
  {
    idVersiones: 2111,
    catalogCodes: ['KAB5060', 'KCB3602'],
    idUnidades: [3488, 3257],
  },
]

export function itemUsesUnidadesDimsOverride(
  idVersiones: number | null | undefined,
  item?: {
    idUnidades?: number
    catalogID?: string | number
    codigo?: string | number
    referencia?: string | number
  } | null,
): boolean {
  if (idVersiones == null || !item) return false
  const code = String(item.catalogID ?? item.codigo ?? item.referencia ?? '')
    .trim()
    .toUpperCase()
  const idU = Number(item.idUnidades)
  for (const ex of UNIDADES_DIMS_JSON_OVERRIDE_EXCEPTIONS) {
    if (ex.idVersiones !== idVersiones) continue
    if (code && ex.catalogCodes.some((c) => c.toUpperCase() === code)) return true
    if (Number.isFinite(idU) && ex.idUnidades.includes(idU)) return true
  }
  return false
}

const PROJECT_UNIDAD_ITEM_TYPES = new Set(['linkedItem', 'linkedSon', 'items'])

function findProjectUnidadForItem(
  item: { idUnidades?: number; catalogID?: string | number; codigo?: string; referencia?: string },
  byId: Map<number, ProjectUnidadDims>,
  byCodigo: Map<string, ProjectUnidadDims>,
): ProjectUnidadDims | undefined {
  const id = Number(item.idUnidades)
  if (Number.isFinite(id) && id > 0 && byId.has(id)) return byId.get(id)
  const keys = [item.catalogID, item.codigo, item.referencia]
  for (const key of keys) {
    if (key == null || key === '') continue
    const hit = byCodigo.get(String(key).trim())
    if (hit) return hit
  }
  return undefined
}

function setSceneItemPlacementY(item: Record<string, any>, y: number) {
  item.y = y
  const mat = item.matrix as { elements?: number[]; setPosition?: (...args: number[]) => void } | undefined
  if (!mat?.elements || mat.elements.length < 14) return
  mat.elements[13] = y
}

export type ApplyProjectUnidadesOptions = {
  /**
   * No pisar height en MBJ/COL con zócalo hijo: tras reconcile el store es cuerpo (800),
   * mientras la unidad antigua puede traer alto 900 (= cuerpo+zócalo).
   */
  skipHeightIfIndependentZocalo?: boolean
  /** Requerido: sin idVersiones / sin excepción → no-op (JSON manda). */
  idVersiones?: number | null
}

/**
 * Solo excepciones (p.ej. proyecto 2111 + KAB5060): medidas desde `unidades`.
 * En todos los demás casos el JSON de escena es la fuente de verdad.
 */
export function applyProjectUnidadesDimensionsToItems(
  items: Map<AnyID, any>,
  unidades: ProjectUnidadDims[] | undefined | null,
  onlyIds?: Iterable<string>,
  options?: ApplyProjectUnidadesOptions,
): number {
  if (!unidades?.length) return 0
  const idVersiones = options?.idVersiones
  if (
    idVersiones == null ||
    !UNIDADES_DIMS_JSON_OVERRIDE_EXCEPTIONS.some((ex) => ex.idVersiones === idVersiones)
  ) {
    return 0
  }
  const onlySet = onlyIds ? new Set([...onlyIds].map(String)) : null
  const byId = new Map<number, ProjectUnidadDims>()
  const byCodigo = new Map<string, ProjectUnidadDims>()
  for (const u of unidades) {
    const id = Number(u.idUnidades)
    if (Number.isFinite(id) && id > 0) byId.set(id, u)
    for (const key of [u.codigo, u.referencia]) {
      if (key != null && String(key).trim()) {
        byCodigo.set(String(key).trim(), u)
      }
    }
  }

  let applied = 0
  items.forEach((item, id) => {
    if (!item || !PROJECT_UNIDAD_ITEM_TYPES.has(String(item.type))) return
    if (onlySet && !onlySet.has(String(id))) return
    if (!itemUsesUnidadesDimsOverride(idVersiones, item)) return
    const unidad = findProjectUnidadForItem(item, byId, byCodigo)
    if (!unidad) return

    const ancho = Number(unidad.ancho)
    const fondo = Number(unidad.fondo)
    const alto = Number(unidad.alto)
    let changed = false

    if (ancho > 0 && Number(item.width) !== ancho) {
      item.width = ancho
      changed = true
    }
    if (fondo > 0 && Number(item.thickness) !== fondo) {
      item.thickness = fondo
      changed = true
    }
    const skipAlto =
      Boolean(options?.skipHeightIfIndependentZocalo) && shouldUseIndependentZocaloUnit(item)
    if (!skipAlto && alto > 0 && Number(item.height) !== alto) {
      item.height = alto
      changed = true
    }

    if (unidad.tipoNormalizado && !item.tipoNormalizado) {
      item.tipoNormalizado = unidad.tipoNormalizado
      changed = true
    }

    // Padres: altura de colocación de la unidad. linkedSon: no tocar y local.
    if (item.type === 'linkedItem' || item.type === 'items') {
      const tipo = String(unidad.tipoNormalizado ?? item.tipoNormalizado ?? '').toUpperCase()
      if (unidad.altura != null && Number(unidad.altura) >= 0) {
        const nextY = Number(unidad.altura)
        if (Number(item.y) !== nextY) {
          setSceneItemPlacementY(item, nextY)
          changed = true
        }
      } else if (tipo === 'MBJ' && Number(item.y) !== 0) {
        setSceneItemPlacementY(item, 0)
        changed = true
      }
    }

    if (ancho > 0 && alto > 0) {
      item.freeDimensionCatalogMm = {
        x: ancho,
        y: alto,
        z: fondo > 0 ? fondo : Number(item.thickness ?? 0),
      }
      changed = true
    }

    if (Array.isArray(item.morphs)) {
      for (const m of item.morphs as Morphs[]) {
        if (m.name === 'Ancho' && ancho > 0) {
          m.default = ancho
          m._default = ancho
        }
        if (m.name === 'Alto' && alto > 0 && !skipAlto) {
          m.default = Number(item.height) > 0 ? Number(item.height) : alto
          m._default = alto
        }
        if (m.name === 'Fondo' && fondo > 0) {
          m.default = fondo
          m._default = fondo
        }
      }
    }

    if (changed) applied += 1
  })

  if (applied > 0) {
    // console.info(
    //   `[applyProjectUnidadesDimensions] Excepción idVersiones=${idVersiones}: ` +
    //     `medidas desde unidades en ${applied} item(s) (JSON incorrecto)`,
    // )
  }
  return applied
}

function findExistingZocaloSonId(parent: LinkedItem, items: Map<AnyID, any>): string | undefined {
  if (typeof parent.independentZocaloSonId === 'string' && parent.independentZocaloSonId) {
    const existing = items.get(parent.independentZocaloSonId as AnyID)
    if (
      existing &&
      isIndependentZocaloLinkedSon(existing) &&
      !(existing as { isIndependentZocaloReturn?: boolean }).isIndependentZocaloReturn
    ) {
      return parent.independentZocaloSonId
    }
  }
  if (!parent.childs?.size) return undefined
  for (const cid of parent.childs) {
    const child = items.get(cid as AnyID)
    if (
      child &&
      isIndependentZocaloLinkedSon(child) &&
      !(child as { isIndependentZocaloReturn?: boolean }).isIndependentZocaloReturn
    ) {
      return String(cid)
    }
  }
  return undefined
}

function findExistingZocaloReturnSonId(
  parent: LinkedItem,
  items: Map<AnyID, any>,
): string | undefined {
  if (
    typeof parent.independentZocaloReturnSonId === 'string' &&
    parent.independentZocaloReturnSonId
  ) {
    return parent.independentZocaloReturnSonId
  }
  if (!parent.childs?.size) return undefined
  for (const cid of parent.childs) {
    const child = items.get(cid as AnyID)
    if ((child as { isIndependentZocaloReturn?: boolean } | undefined)?.isIndependentZocaloReturn) {
      return String(cid)
    }
  }
  return undefined
}

/**
 * Rinconeros: segunda tira del zócalo en L (retorno en el extremo del frontal).
 * Mismo alto/retranqueo que el zócalo de los MBJ que colisionan en ese lateral.
 */
export function attachRinconeroIndependentZocaloReturn(args: {
  get: () => StoreSlice
  set: (fn: (store: StoreSlice) => void) => void
  parentId: string
  zocaloGlb: any
  zocaloBack: any
  zocaloExtra?: { zonas?: { idAcabadoUnidad: number; zona: string }[] }
}): string | null {
  const { get, set, parentId, zocaloGlb, zocaloBack, zocaloExtra } = args
  const parent = get().items.get(parentId as AnyID) as LinkedItem | undefined
  if (!parent || !getRinconeroIndependentZocaloLayout(parent)) return null

  const existingReturnId = findExistingZocaloReturnSonId(parent, get().items)
  if (existingReturnId) {
    set((store) => {
      const draft = store.items.get(parentId as AnyID) as LinkedItem | undefined
      const ret = store.items.get(existingReturnId as AnyID) as any
      if (!draft || !ret) return
      ret.isIndependentZocaloReturn = true
      ret.visible = true
      draft.independentZocaloReturnSonId = existingReturnId
      const cached = store.sceneGLB.get(String(ret.catalogID))
      const glbRoot = cached?.glb?.scene ?? cached?.glb ?? zocaloGlb?.scene ?? zocaloGlb
      placeLinkedIndependentZocalo(ret, draft, {
        glbSizeMm: glbRoot
          ? vector3ToFreeDimensionGlbBaseMm(getGLBSize(glbRoot))
          : undefined,
        backGLBInfo: (cached?.backGLBInfo ?? zocaloBack) as
          | { ancho?: number; alto?: number; fondo?: number }
          | undefined,
        items: store.items,
      })
    })
    return existingReturnId
  }

  const frontId = findExistingZocaloSonId(parent, get().items)
  const front = frontId ? (get().items.get(frontId as AnyID) as any) : null
  const linkedID = getId('linkedSon')
  let linkedSon: any
  try {
    linkedSon = newGetDataFromGLB({
      id: linkedID,
      glbData: zocaloGlb,
      type: 'linkedSon',
      catalogID: zocaloBack?.codigo ?? INDEPENDENT_ZOCALO_CATALOG_ID,
      firstParentID: parentId,
      visible: true,
      backGLBInfo: zocaloBack,
      extraInfo: zocaloExtra ?? { zonas: [] },
      itemInfo: {
        x: 0,
        y: 0,
        z: 0,
        parentOption: 0,
        option: INDEPENDENT_ZOCALO_RETURN_OPTION,
        visualOrder: 998,
        typeOfSon: 'Zocalo',
        description: zocaloBack?.descripcion ?? 'Zocalo',
        handPosition: 'Normal',
        matrix: new Matrix4().setPosition(0, 0, 0),
      },
      canCollide: false,
    }) as any
  } catch (e) {
    // console.warn('[attachRinconeroZocaloReturn] Error creando retorno para', parent.catalogID, e)
    return null
  }

  linkedSon.isIndependentZocalo = true
  linkedSon.isIndependentZocaloReturn = true
  linkedSon.visible = true
  if (front && Array.isArray(front.textures) && front.textures.length) {
    linkedSon.textures = structuredClone
      ? structuredClone(front.textures)
      : JSON.parse(JSON.stringify(front.textures))
  }

  set((store) => {
    const draft = store.items.get(parentId as AnyID) as LinkedItem | undefined
    if (!draft) return

    const zocaloScene = zocaloGlb?.scene ?? zocaloGlb
    placeLinkedIndependentZocalo(linkedSon, draft, {
      glbSizeMm: zocaloScene
        ? vector3ToFreeDimensionGlbBaseMm(getGLBSize(zocaloScene))
        : undefined,
      backGLBInfo: zocaloBack as { ancho?: number; alto?: number; fondo?: number },
      items: store.items,
    })

    draft.independentZocaloReturnSonId = linkedID
    if (!draft.childs) draft.childs = new Set()
    draft.childs.add(linkedID)
    const orderKey =
      `0-${INDEPENDENT_ZOCALO_RETURN_OPTION}-998!${linkedID}` as `${number}-${number}-${number}!${string}`
    if (!draft.childrenOrder) draft.childrenOrder = []
    if (!draft.childrenOrder.some((k) => String(k).includes(linkedID))) {
      draft.childrenOrder = [...draft.childrenOrder, orderKey]
    }

    linkedSon.increment = '0'
    store.items.set(linkedID, linkedSon)
    const payload = { glb: zocaloGlb, backGLBInfo: zocaloBack }
    for (const key of independentZocaloSceneGlbKeys(linkedSon.catalogID ?? zocaloBack?.codigo)) {
      store.sceneGLB.set(key, payload)
    }
  })

  return linkedID
}

function morphRangeOpen(m: Morphs): boolean {
  const min = Number(m.min ?? 0)
  const max = Number(m.max ?? 0)
  return max > min
}

/** Fuerza glbBase/catalog/scaleBase al asset actual (sobrescribe JSON viejo con zócalo embebido). */
function refreshParentGlbBasesFromScene(
  parent: LinkedItem,
  sceneEntry: { glb: any; backGLBInfo: any } | undefined,
) {
  const back = sceneEntry?.backGLBInfo as
    | { ancho?: number; alto?: number; fondo?: number; referenciaBlender?: string; morphs?: unknown }
    | undefined
  const glbRoot = sceneEntry?.glb?.scene ?? sceneEntry?.glb
  if (glbRoot) {
    const fullGlbSize = getGLBSize(glbRoot)
    // Laterales/MBJ: base de escala = Costado o sin losa Zocalo (no AABB con zócalo).
    const glbSize =
      usesFreeDimensionScaling(parent) &&
      (shouldUseIndependentZocaloUnit(parent) || isFreeDimensionLateralOrCostadoCatalog(parent))
        ? getGLBSizeForFreeDimension(
            glbRoot,
            fullGlbSize,
            (back as { acabados?: import('@types').Acabados[] } | undefined)?.acabados,
            parent.catalogID ?? parent.codigo,
          )
        : fullGlbSize
    if (glbSize?.x > 0 && glbSize?.y > 0) {
      parent.glbSize = glbSize
      parent.freeDimensionGlbBaseMm = vector3ToFreeDimensionGlbBaseMm(glbSize)
    }
  }

  const catX = Number(back?.ancho ?? parent.freeDimensionCatalogMm?.x ?? parent.width ?? 0)
  const catY = Number(back?.alto ?? parent.freeDimensionCatalogMm?.y ?? parent.height ?? 0)
  const catZ = Number(back?.fondo ?? parent.freeDimensionCatalogMm?.z ?? parent.thickness ?? 0)
  if (catX > 0 && catY > 0) {
    parent.freeDimensionCatalogMm = {
      x: catX,
      y: catY,
      z: catZ > 0 ? catZ : parent.thickness || 0,
    }
  } else {
    ensureFreeDimensionCatalogBases(parent)
  }

  // Si cabecera API y mesh discrepan (RA805 fondo 600 vs glb 350), el contrato nuevo es el GLB
  // SOLO cuando el mesh es más pequeño. Un AABB mayor (rinconero L + zócalo integrado:
  // 820 vs fondo API 600) no debe inflar el catálogo: la encimera se encogía proporcionalmente
  // (720 × 600/820 ≈ 527 mm) y el cuerpo se veía más pequeño que las tiras de zócalo.
  // MAL/KAB: la unidad (ancho/alto/fondo API) manda.
  // Rinconero: catálogo = plano puerta/ocupación (1000×900×600), no AABB de carcasa.
  if (
    parent.freeDimensionCatalogMm &&
    parent.freeDimensionGlbBaseMm &&
    !isMalFreeDimensionUnit(parent) &&
    !isRinconeroCatalog(parent.catalogID)
  ) {
    const c = parent.freeDimensionCatalogMm
    const g = parent.freeDimensionGlbBaseMm
    const diverge = (a: number, b: number) =>
      a > 0 && b > 0 && Math.abs(a - b) > Math.max(a, b) * 0.15
    if (diverge(c.z, g.z) && g.z < c.z) c.z = g.z
    if (diverge(c.y, g.y) && g.y < c.y) c.y = g.y
    if (diverge(c.x, g.x) && g.x < c.x) c.x = g.x
  }

  if (parent.freeDimensionCatalogMm && parent.freeDimensionGlbBaseMm) {
    parent.freeDimensionScaleBaseMm = resolveFreeDimensionScaleBaseMm(
      parent.freeDimensionCatalogMm,
      parent.freeDimensionGlbBaseMm,
    )
  }

  if (back?.referenciaBlender) {
    parent.glbPath = back.referenciaBlender
  }
}

/**
 * Morphs + flags como newGetDataFromGLB para que editMorph / sidebar / scale
 * se comporten igual que una unidad recién dibujada.
 */
function hydrateFreeDimensionItemParity(
  item: LinkedItem & Record<string, any>,
  sceneEntry: { glb: any; backGLBInfo: any } | undefined,
) {
  if (!usesFreeDimensionScaling(item)) return

  refreshParentGlbBasesFromScene(item, sceneEntry)
  const back = sceneEntry?.backGLBInfo

  const pure = usesPureGroupFreeDimensionScale(item)
  if (pure) {
    item.hasMorphs = Boolean(back?.morphs)
    item.freeDimensionAnchoViaGlbMorph = false
    item.freeDimensionAltoViaGlbMorph = false
    item.freeDimensionFondoViaGlbMorph = false
  }

  // Sin morphs en JSON (KBB/RC antiguos morphs:null) editMorph no hace nada → sellar.
  const needsMorphRebuild =
    !Array.isArray(item.morphs) ||
    item.morphs.length === 0 ||
    !item.morphs.some((m: Morphs) => m.name === 'Ancho') ||
    !item.morphs.some((m: Morphs) => m.name === 'Alto') ||
    !item.morphs.some((m: Morphs) => m.name === 'Fondo')

  if (needsMorphRebuild && back) {
    try {
      const fromApi = getMorphsFromGLB(back)
      const liveByName: Record<string, number> = {
        Ancho: Number(item.width ?? 0),
        Alto: Number(item.height ?? 0),
        Fondo: Number(item.thickness ?? 0),
      }
      const cat = item.freeDimensionCatalogMm
      const catalogByName: Record<string, number> = {
        Ancho: Number(cat?.x ?? liveByName.Ancho),
        Alto: Number(cat?.y ?? liveByName.Alto),
        Fondo: Number(cat?.z ?? liveByName.Fondo),
      }
      item.morphs = fromApi.map((m) => {
        if (m.name !== 'Ancho' && m.name !== 'Alto' && m.name !== 'Fondo') return m
        const live = liveByName[m.name] || Number(m.default ?? 0)
        const catalog = catalogByName[m.name] || live
        return {
          ...m,
          // Ancla de scale (pure-group): catálogo. Layout guardado en default (p.ej. 551).
          // NO ampliar min/max aquí: abajo applyFreeDimensionUniversalStyleMorphs
          // (como drawItem) abre Ancho RB/RC (min=10…max=cat) si hace falta.
          _default: catalog > 0 ? catalog : live,
          default: live > 0 ? live : catalog,
          modifyable: Boolean(m.modifyable),
          show: true,
        }
      })
    } catch (e) {
      // console.warn('[hydrateFreeDimensionItemParity] morphs', item.catalogID, e)
    }
  } else if (Array.isArray(item.morphs) && item.freeDimensionCatalogMm && pure) {
    // MBJ/COL/MAL: _default = ancla catálogo; default = layout. Sin tocar min/max.
    for (const m of item.morphs as Morphs[]) {
      if (m.name === 'Ancho') {
        const catX = Number(item.freeDimensionCatalogMm.x ?? 0)
        if (catX > 0) m._default = catX
        if (Number(item.width) > 0) m.default = Number(item.width)
        if (back && typeof back.modificarAncho === 'boolean') {
          m.modifyable = Boolean(back.modificarAncho)
        }
      }
      if (m.name === 'Alto') {
        const catY = Number(item.freeDimensionCatalogMm.y ?? 0)
        if (catY > 0) m._default = catY
        if (Number(item.height) > 0) m.default = Number(item.height)
        if (back && typeof back.modificarAlto === 'boolean') {
          m.modifyable = Boolean(back.modificarAlto)
        }
      }
      if (m.name === 'Fondo') {
        const catZ = Number(item.freeDimensionCatalogMm.z ?? 0)
        if (catZ > 0) m._default = catZ
        if (Number(item.thickness) > 0) m.default = Number(item.thickness)
        if (back && typeof back.modificarFondo === 'boolean') {
          m.modifyable = Boolean(back.modificarFondo)
        }
      }
    }
  }

  // RB/RC/regletas: mismo contrato que newGetDataFromGLB.
  // Sin morphs de malla (hasMorphs=false): escala grupo width/glb (50/100=0.5).
  // NO forzar anchoViaGlbMorph: ese path solo escala por encima de max y deja visual a 100.
  if (!pure && Array.isArray(item.morphs) && item.freeDimensionCatalogMm && item.freeDimensionGlbBaseMm) {
    const cat = item.freeDimensionCatalogMm
    const glb = item.freeDimensionGlbBaseMm
    const layoutW = Number(item.width ?? 0)
    const layoutH = Number(item.height ?? 0)
    const layoutT = Number(item.thickness ?? 0)
    const altoOffsetMm = getFreeDimensionAltoOffsetMm(item)
    const regleta = isRbFreeDimensionRegleta(item)
    const tpcNoMorph = isFreeDimensionTpcNoMorphCatalog(item)
    // Frontal/TPC: siempre escala grupo (proyectos antiguos con fondoViaMorph
    // dejaban Alto/Fondo sin efecto visual aunque el sidebar aceptara valores).
    const anchoVia = tpcNoMorph || regleta
      ? false
      : resolveFreeDimensionAnchoViaGlbMorph(glb, cat, layoutW)
    const altoVia = tpcNoMorph
      ? false
      : resolveFreeDimensionAltoViaGlbMorph(glb, cat, altoOffsetMm)
    const fondoVia = tpcNoMorph
      ? false
      : resolveFreeDimensionFondoViaGlbMorph(glb, cat, item.catalogID)
    item.freeDimensionAnchoViaGlbMorph = anchoVia
    item.freeDimensionAltoViaGlbMorph = altoVia
    item.freeDimensionFondoViaGlbMorph = fondoVia
    item.morphs = applyFreeDimensionUniversalStyleMorphs(
      item.morphs as Morphs[],
      { width: layoutW, height: layoutH, thickness: layoutT },
      glb,
      cat,
      anchoVia,
      altoVia,
      fondoVia,
      altoOffsetMm,
      itemUsesFreeDimensionAltoApiMorph(item),
      isFreeDimensionAnchoApiMorphCatalog(item),
      false,
    )
    item.hasMorphs = tpcNoMorph ? false : Boolean(back?.morphs)
  }

  // KAB/MAL antiguos: height/thickness del JSON no coinciden con GLB nuevo
  // (p.ej. height 400 vs cat 800, thickness 600 vs glb 350) → scale absurdo.
  // NO pisar anchos/altos intencionalmente modificados (KCB360 551 vs cat 600).
  reconcileStaleLayoutDimsToNewCatalog(item)
}

/**
 * Si height/thickness guardados producen scale extremo vs catálogo/GLB actual,
 * alinear al tamaño de catálogo (como una unidad recién dibujada).
 * Ej. KAB80602ES legacy: height 400 / cat 800, thickness 600 / glb 350.
 *
 * Ancho: NUNCA pisar width>0 del JSON.
 *
 * Alto legacy bajas: JSON 900 (=cuerpo+zócalo) → store height = cuerpo 800 + hijo zócalo 100.
 * RB: store height = tope encimera (900), no cuerpo.
 */
function reconcileStaleLayoutDimsToNewCatalog(item: LinkedItem & Record<string, any>): boolean {
  const cat = item.freeDimensionCatalogMm
  const glb = item.freeDimensionGlbBaseMm
  if (!cat && !glb) return false

  const malUnit = isMalFreeDimensionUnit(item)
  const pickTarget = (catalogMm: number | undefined, glbMm: number | undefined): number => {
    const c = catalogMm != null && catalogMm > 0 ? catalogMm : 0
    const g = glbMm != null && glbMm > 0 ? glbMm : 0
    if (c > 0 && g > 0 && Math.abs(c - g) > Math.max(c, g) * 0.15) {
      // MAL: unidad API (KAB5060 alto 500 / fondo 350). RA*: confiar en el GLB.
      return malUnit ? c : g
    }
    return c || g
  }

  const targetX = pickTarget(cat?.x, glb?.x)
  const targetY = pickTarget(cat?.y, glb?.y)
  const targetZ = pickTarget(cat?.z, glb?.z)
  if (targetX <= 0 || targetY <= 0) return false

  const STALE_LO = 0.72
  const STALE_HI = 1.38
  let changed = false

  const fixAxis = (live: number, targetMm: number, apply: (v: number) => void): boolean => {
    if (targetMm <= 0) return false
    if (!(live > 0)) {
      apply(targetMm)
      return true
    }
    const ratio = live / targetMm
    // Solo ratios extremos (corrupción). 551/600≈0.92 es resize legítimo → conservar.
    if (ratio < STALE_LO || ratio > STALE_HI) {
      apply(Math.round(targetMm))
      return true
    }
    return false
  }

  // Ancho: NUNCA pisar un width>0 del JSON (cortes CORTEKIT, KAB redimensionados…).
  // Scale = widthGuardado / catálogoNuevo.
  const liveWidth = Number(item.width ?? 0)
  if (!(liveWidth > 0) && targetX > 0) {
    item.width = Math.round(targetX)
    changed = true
  }

  // Alto legacy = cuerpo + zócalo (900) con bases nuevas a cuerpo (800):
  // 900/800=1.125 cae dentro de STALE_* y no se tocaba → scaleY 1.125 + lift = caos.
  // Usar shouldUseIndependentZocaloUnit (no solo el flag) para cubrir el 1er hydrate
  // antes de migrateLegacyIndependentZocalosOnLoad.
  const liveH = Number(item.height ?? 0)
  const zocaloLift = Number(item.independentZocaloHeightMm ?? INDEPENDENT_ZOCALO_HEIGHT_MM)
  const catalogY = Number(cat?.y ?? 0)

  // Costados/laterales (CB9060, CC24060, LB8060…): store = catálogo completo.
  // Sync antiguo o JSON dejó cuerpo (cat−100): 900→800 / 2400→2300. 800/900≈0.89
  // no dispara STALE_* → hay que restaurar explícitamente.
  if (
    isFreeDimensionLateralOrCostadoCatalog(item) &&
    catalogY > 0 &&
    liveH > 0 &&
    zocaloLift > 0 &&
    Math.abs(liveH - (catalogY - zocaloLift)) <= 2
  ) {
    item.height = Math.round(catalogY)
    changed = true
  } else {
    const willHaveIndepZocalo =
      item.hasIndependentZocalo === true ||
      (typeof item.independentZocaloSonId === 'string' && item.independentZocaloSonId.length > 0) ||
      shouldUseIndependentZocaloUnit(item)
    const catalogIsBodySized =
      Boolean(cat?.y) &&
      Boolean(glb?.y) &&
      Math.abs(Number(cat!.y) - Number(glb!.y)) <= 2
    if (
      isRbFreeDimensionRegleta(item) &&
      !isRb805Catalog(item) &&
      !String(item.catalogID ?? item.codigo ?? '').toUpperCase().startsWith('RC') &&
      catalogIsBodySized &&
      targetY > 0
    ) {
      // RB* salvo RB805: store height = tope encimera (p.ej. 900).
      const furnitureTop = targetY + (zocaloLift > 0 ? zocaloLift : INDEPENDENT_ZOCALO_HEIGHT_MM)
      if (liveH > 0 && Math.abs(liveH - targetY) <= 2 && furnitureTop > targetY) {
        item.height = Math.round(furnitureTop)
        changed = true
      }
    } else if (
      isRbFreeDimensionRegleta(item) &&
      String(item.catalogID ?? item.codigo ?? '').toUpperCase().startsWith('RC') &&
      catalogY > 0 &&
      liveH > 0 &&
      (Math.abs(liveH - (catalogY - 100)) <= 2 || Math.abs(liveH - (catalogY + 100)) <= 2)
    ) {
      // RC legacy: JSON/sidebar con ±100 (2200 o 2400) → alto de unidad (2300).
      item.height = Math.round(catalogY)
      changed = true
    } else if (
      willHaveIndepZocalo &&
      catalogIsBodySized &&
      liveH > 0 &&
      targetY > 0 &&
      zocaloLift > 0 &&
      Math.abs(liveH - (targetY + zocaloLift)) <= 2
    ) {
      // Legacy bajas: height mueble completo (900) → cuerpo catálogo (800).
      // El zócalo hijo aporta los 100 mm → visual total sigue en 900.
      item.height = Math.round(targetY)
      changed = true
    } else if (
      willHaveIndepZocalo &&
      catalogIsBodySized &&
      liveH > 0 &&
      targetY > 0 &&
      zocaloLift > 0 &&
      Math.abs(liveH - (targetY - zocaloLift)) <= 2
    ) {
      // Caso inverso (KCFB372602): catálogo≈GLB=2300 (ya es cuerpo) pero el JSON/hidratación
      // dejó height=2200 (=2300−100). Eso NO es “cuerpo”; es un −100 erróneo.
      // Restaurar a catálogo; el zócalo hijo suma aparte.
      item.height = Math.round(targetY)
      changed = true
    } else if (
      // Frontal/TPC: tiras 50→100/150 son layout legítimo (ratio 2–3). No pisar a catálogo.
      !isFreeDimensionTpcNoMorphCatalog(item) &&
      fixAxis(liveH, targetY, (v) => {
        item.height = v
      })
    ) {
      changed = true
    }
  }

  // Fondo mueble: conservar el guardado (cortes / resize). Solo rellenar si falta.
  // Excepción RA*: JSON a veces trae fondo API (600) con mesh real ~350.
  const liveT = Number(item.thickness ?? 0)
  const glbZ = Number(glb?.z ?? 0)
  const isRaColumn = /^RA\d/i.test(String(item.catalogID ?? item.codigo ?? ''))
  if (!(liveT > 0) && targetZ > 0) {
    item.thickness = Math.round(targetZ)
    changed = true
  } else if (
    isRaColumn &&
    glbZ > 0 &&
    liveT > 0 &&
    (liveT / glbZ < STALE_LO || liveT / glbZ > STALE_HI)
  ) {
    item.thickness = Math.round(glbZ)
    changed = true
  }

  if (changed && Array.isArray(item.morphs)) {
    for (const m of item.morphs as Morphs[]) {
      if (m.name === 'Ancho') {
        m.default = item.width
        m._default = targetX
      } else if (m.name === 'Alto') {
        m.default = item.height
        m._default = targetY
      } else if (m.name === 'Fondo') {
        m.default = item.thickness
        m._default = targetZ > 0 ? targetZ : m._default
      }
    }
  }

  // glbSize legacy corrupto (p.ej. {10,1,1}): reconstruir desde glbBase mm.
  const gs = item.glbSize as { x?: number; y?: number; z?: number } | undefined
  if (
    glb &&
    glb.x > 0 &&
    gs &&
    (Number(gs.x) > 5 || Number(gs.y) > 5 || Number(gs.z) > 5 || Number(gs.x) <= 0)
  ) {
    item.glbSize = {
      x: glb.x / 1000,
      y: glb.y / 1000,
      z: glb.z / 1000,
    }
    changed = true
  }

  if (changed) {
    // console.info('[reconcileStaleLayoutDims]', item.catalogID, {
    //   width: item.width,
    //   height: item.height,
    //   thickness: item.thickness,
    //   target: { x: targetX, y: targetY, z: targetZ },
    // })
  }
  return changed
}

function collectFreeDimParentIds(
  items: Map<AnyID, any>,
  onlyParentIds?: Iterable<string>,
): string[] {
  const onlySet = onlyParentIds ? new Set(onlyParentIds) : null
  const ids: string[] = []
  items.forEach((item, id) => {
    if (!item || item.type !== 'linkedItem') return
    if (onlySet && !onlySet.has(String(id))) return
    if (!usesFreeDimensionScaling(item)) return
    ids.push(String(id))
  })
  return ids
}

function applyKbrLegacyRegletaPlus20OnLoad({ get, set, onlyParentIds }: MigrateDeps): number {
  const onlySet = onlyParentIds ? new Set([...onlyParentIds].map(String)) : null
  let bumped = 0
  set((store) => {
    store.items.forEach((item, id) => {
      if (!item || item.type !== 'linkedItem') return
      if (onlySet && !onlySet.has(String(id))) return
      if (applyKbrLegacyRegletaPlus20(item as LinkedItem)) bumped += 1
    })
  })
  return bumped
}

/** Hidrata bases/flags/morphs de todos los free-dim (muebles + laterales). */
export function hydrateFreeDimensionItemsFromScene({
  get,
  set,
  onlyParentIds,
}: MigrateDeps): number {
  const ids = collectFreeDimParentIds(get().items, onlyParentIds)
  if (ids.length === 0) return 0

  set((store) => {
    for (const id of ids) {
      const item = store.items.get(id as AnyID) as LinkedItem | undefined
      if (!item) continue
      const key = item.catalogID || item.idUnidades
      const sceneEntry = key != null ? store.sceneGLB.get(key) : undefined
      hydrateFreeDimensionItemParity(item as LinkedItem & Record<string, any>, sceneEntry)
    }
  })

  // console.info(
  //   `[hydrateFreeDimensionItems] ${ids.length} unidad(es) free-dim alineadas a drawItem`,
  // )
  return ids.length
}

async function loadZocaloCatalogAssets(sceneGlbMap: StoreSlice['sceneGLB']): Promise<{
  zocaloBack: any
  zocaloGlb: any
} | null> {
  const zocaloCatalog = INDEPENDENT_ZOCALO_CATALOG_ID
  let zocaloBack = sceneGlbMap.get(zocaloCatalog)?.backGLBInfo
  let zocaloGlb = sceneGlbMap.get(zocaloCatalog)?.glb
  if (!zocaloBack || !zocaloGlb) {
    try {
      zocaloBack = await getGLB(sceneGlbMap, zocaloCatalog)
    } catch (e) {
      console.error('[Zocalo4000] getGLB falló:', zocaloCatalog, e)
      return null
    }
    if (zocaloBack?.referenciaBlender) {
      try {
        const loader = await setGLTFLoader()
        zocaloGlb = await loader.loadAsync(zocaloBack.referenciaBlender)
      } catch (e) {
        console.error(
          '[Zocalo4000] loadAsync falló:',
          zocaloBack.referenciaBlender,
          e,
        )
        return null
      }
    }
  }
  if (!zocaloBack || !zocaloGlb) {
    console.error('[Zocalo4000] No se pudo cargar unidad/GLB', {
      catalog: zocaloCatalog,
      hasBack: Boolean(zocaloBack),
      hasGlb: Boolean(zocaloGlb),
      referenciaBlender: zocaloBack?.referenciaBlender,
    })
    return null
  }
  return { zocaloBack, zocaloGlb }
}

/**
 * Proyectos antiguos: mismo catalogID/URL de GLB, pero el archivo ya es cuerpo sin zócalo.
 * drawItem añade ZOCALO 4000; al abrir hay que hacer lo mismo para MBJ/COL sin hijo zócalo.
 */
export async function migrateLegacyIndependentZocalosOnLoad({
  get,
  set,
  onlyParentIds,
}: MigrateDeps): Promise<number> {
  const onlySet = onlyParentIds ? new Set(onlyParentIds) : null
  const parentIds: string[] = []

  get().items.forEach((item, id) => {
    if (!item || item.type !== 'linkedItem') return
    if (onlySet && !onlySet.has(String(id))) return
    if (!shouldUseIndependentZocaloUnit(item)) return
    parentIds.push(String(id))
  })

  if (parentIds.length === 0) return 0

  const assets = await loadZocaloCatalogAssets(get().sceneGLB)
  if (!assets) {
    return 0
  }

  const { zocaloBack, zocaloGlb } = assets
  const zocaloExtra: { zonas: { idAcabadoUnidad: number; zona: string }[] } = { zonas: [] }
  if (zocaloBack?.idUnidades) {
    try {
      const concha = await API().api.acabadosGetUnidadAcabadosInfo(zocaloBack.idUnidades)
      if (concha && Array.isArray(concha.data?.zonas)) {
        zocaloExtra.zonas = concha.data.zonas as {
          idAcabadoUnidad: number
          zona: string
        }[]
      }
    } catch {
      /* zonas opcionales */
    }
  }

  let migrated = 0

  for (const parentId of parentIds) {
    const parent = get().items.get(parentId as AnyID) as LinkedItem | undefined
    if (!parent || !shouldUseIndependentZocaloUnit(parent)) continue

    const existingId = findExistingZocaloSonId(parent, get().items)
    if (existingId) {
      set((store) => {
        const draft = store.items.get(parentId as AnyID) as LinkedItem | undefined
        const existing = store.items.get(existingId as AnyID) as any
        if (!draft || !existing) return
        draft.hasIndependentZocalo = true
        draft.independentZocaloHeightMm = INDEPENDENT_ZOCALO_HEIGHT_MM
        draft.independentZocaloSonId = existingId
        revertIndependentZocaloFloorOffsetIfNeeded(draft)
        raiseParentForIndependentZocalo(draft)
        const catalogKey = draft.catalogID || draft.idUnidades
        refreshParentGlbBasesFromScene(
          draft,
          catalogKey != null ? store.sceneGLB.get(catalogKey) : undefined,
        )
        const cached = store.sceneGLB.get(String(existing.catalogID))
        const glbRoot = cached?.glb?.scene ?? cached?.glb
        placeLinkedIndependentZocalo(existing, draft, {
          glbSizeMm: glbRoot
            ? vector3ToFreeDimensionGlbBaseMm(getGLBSize(glbRoot))
            : undefined,
          backGLBInfo: cached?.backGLBInfo as
            | { ancho?: number; alto?: number; fondo?: number }
            | undefined,
          items: store.items,
        })
      })
      attachRinconeroIndependentZocaloReturn({
        get,
        set,
        parentId,
        zocaloGlb,
        zocaloBack,
        zocaloExtra,
      })
      continue
    }

    const linkedID = getId('linkedSon')
    let linkedSon: any
    try {
      linkedSon = newGetDataFromGLB({
        id: linkedID,
        glbData: zocaloGlb,
        type: 'linkedSon',
        catalogID: zocaloBack.codigo ?? INDEPENDENT_ZOCALO_CATALOG_ID,
        firstParentID: parentId,
        visible: true,
        backGLBInfo: zocaloBack,
        extraInfo: zocaloExtra,
        itemInfo: {
          x: 0,
          y: 0,
          z: 0,
          parentOption: 0,
          option: 9900,
          visualOrder: 999,
          typeOfSon: 'Zocalo',
          description: zocaloBack.descripcion ?? 'Zocalo',
          handPosition: 'Normal',
          matrix: new Matrix4().setPosition(0, 0, 0),
        },
        canCollide: false,
      }) as any
    } catch {
      continue
    }

    set((store) => {
      const draft = store.items.get(parentId as AnyID) as LinkedItem | undefined
      if (!draft) return

      draft.hasIndependentZocalo = true
      draft.independentZocaloHeightMm = INDEPENDENT_ZOCALO_HEIGHT_MM
      draft.independentZocaloSonId = linkedID
      revertIndependentZocaloFloorOffsetIfNeeded(draft)
      raiseParentForIndependentZocalo(draft)

      const catalogKey = draft.catalogID || draft.idUnidades
      refreshParentGlbBasesFromScene(
        draft,
        catalogKey != null ? store.sceneGLB.get(catalogKey) : undefined,
      )

      const zocaloScene = zocaloGlb?.scene ?? zocaloGlb
      placeLinkedIndependentZocalo(linkedSon, draft, {
        glbSizeMm: zocaloScene
          ? vector3ToFreeDimensionGlbBaseMm(getGLBSize(zocaloScene))
          : undefined,
        backGLBInfo: zocaloBack as { ancho?: number; alto?: number; fondo?: number },
        items: store.items,
      })

      if (!draft.childs) draft.childs = new Set()
      draft.childs.add(linkedID)
      const orderKey = `0-9900-999!${linkedID}` as `${number}-${number}-${number}!${string}`
      if (!draft.childrenOrder) draft.childrenOrder = []
      draft.childrenOrder = [...draft.childrenOrder, orderKey]

      linkedSon.increment = '0'
      store.items.set(linkedID, linkedSon)
      const payload = { glb: zocaloGlb, backGLBInfo: zocaloBack }
      for (const key of independentZocaloSceneGlbKeys(linkedSon.catalogID ?? zocaloBack?.codigo)) {
        store.sceneGLB.set(key, payload)
      }
    })

    attachRinconeroIndependentZocaloReturn({
      get,
      set,
      parentId,
      zocaloGlb,
      zocaloBack,
      zocaloExtra,
    })

    migrated += 1
  }

  return migrated
}

function isPreferredRbEncimeraSon(encimera: {
  catalogID?: string | number
  code?: string
  referencia?: string
  description?: string
  typeOfSon?: string
}): boolean {
  return matchesRbRegletaPreferredEncimera({
    descripcion: String(encimera.catalogID ?? encimera.code ?? encimera.referencia ?? ''),
    unidad: {
      codigo: String(encimera.catalogID ?? ''),
      referencia: String(encimera.referencia ?? ''),
    },
  })
}

async function loadRbPreferredEncimeraAssets(
  sceneGLB: StoreSlice['sceneGLB'],
): Promise<{ encBack: any; encGlb: any } | null> {
  const code = RB_REGLETA_ENCIMERA_CODIGO
  const cached = sceneGLB.get(code) || sceneGLB.get(String(code))
  let encBack = cached?.backGLBInfo ?? null
  let encGlb = cached?.glb ?? null

  if (!encBack) {
    try {
      encBack = await getGLB(sceneGLB, code)
    } catch (e) {
      console.error('[RB encimera] getGLB falló:', code, e)
      return null
    }
  }

  if (!encGlb && encBack?.referenciaBlender) {
    try {
      const loader = await setGLTFLoader()
      encGlb = await loader.loadAsync(encBack.referenciaBlender)
    } catch (e) {
      console.error('[RB encimera] loadAsync falló:', encBack.referenciaBlender, e)
      return null
    }
  }

  if (!encBack || !encGlb) return null
  return { encBack, encGlb }
}

function resolveRbEncimeraBackendPosicionY(
  parent: LinkedItem,
  sceneGLB: StoreSlice['sceneGLB'],
): number | undefined {
  const catalogKey = parent.catalogID || parent.idUnidades
  const entry =
    (catalogKey != null ? sceneGLB.get(catalogKey) : undefined) ||
    (parent.catalogID != null ? sceneGLB.get(String(parent.catalogID)) : undefined) ||
    (parent.idUnidades != null ? sceneGLB.get(parent.idUnidades) : undefined)
  const back = entry?.backGLBInfo as
    | {
        alto?: number
        elementosEnlazados?: Array<{ descripcion?: string | null; posicionY?: number | null }>
        elementosEnlazadosAcabadosPorDefecto?: Array<{
          descripcion?: string | null
          posicionY?: number | null
        }>
      }
    | undefined
  const elementos =
    back?.elementosEnlazados ?? back?.elementosEnlazadosAcabadosPorDefecto ?? []
  const enc = elementos.find((e) => /encimera/i.test(String(e.descripcion ?? '')))
  if (typeof enc?.posicionY === 'number' && enc.posicionY > 0) return enc.posicionY

  const bodyMm = parent.freeDimensionCatalogMm?.y ?? back?.alto ?? 0
  const placement = getFreeDimensionPlacementStoreAltoMm(back as any, bodyMm, parent.catalogID, {
    hasIndependentZocalo: parent.hasIndependentZocalo,
    tipoNormalizado: parent.tipoNormalizado,
  })
  return placement > 0 ? placement : undefined
}

function restoreRbParentFurnitureTopHeight(
  parent: LinkedItem & Record<string, any>,
  sceneGLB: StoreSlice['sceneGLB'],
): boolean {
  if (!isRbFreeDimensionRegleta(parent)) return false
  if (String(parent.catalogID ?? parent.codigo ?? '').toUpperCase().startsWith('RC')) {
    return false
  }
  const bodyMm = Number(parent.freeDimensionCatalogMm?.y ?? 0)
  if (isRb805Catalog(parent) && bodyMm > 0) {
    if (Math.abs(Number(parent.height ?? 0) - bodyMm) <= 1) return false
    parent.height = Math.round(bodyMm)
    return true
  }
  const backendY = resolveRbEncimeraBackendPosicionY(parent, sceneGLB)
  const zocaloH = Number(parent.independentZocaloHeightMm ?? INDEPENDENT_ZOCALO_HEIGHT_MM)
  const target =
    typeof backendY === 'number' && backendY > bodyMm
      ? backendY
      : bodyMm > 0 && zocaloH > 0
        ? bodyMm + zocaloH
        : 0
  if (target <= 0) return false
  if (Math.abs(Number(parent.height ?? 0) - target) <= 1) return false
  parent.height = Math.round(target)
  return true
}

/**
 * Proyectos antiguos RB*: API/JSON guardó Encimera B10 (tira ~50 mm).
 * drawItem fuerza Encimera B80new — al abrir hay que hacer el mismo swap.
 */
export async function migrateLegacyRbEncimeraB10ToB80newOnLoad({
  get,
  set,
  onlyParentIds,
}: MigrateDeps): Promise<number> {
  const onlySet = onlyParentIds ? new Set([...onlyParentIds].map(String)) : null
  const parentIds: string[] = []

  get().items.forEach((item, id) => {
    if (!item || item.type !== 'linkedItem') return
    if (onlySet && !onlySet.has(String(id))) return
    if (!isRbFreeDimensionRegleta(item) || !item.childs?.size) return
    parentIds.push(String(id))
  })

  if (parentIds.length === 0) return 0

  let needsPreferredAssets = false
  for (const parentId of parentIds) {
    const parent = get().items.get(parentId as AnyID) as LinkedItem | undefined
    if (!parent?.childs) continue
    for (const cid of parent.childs) {
      const son = get().items.get(cid as AnyID)
      if (!son || !isEncimeraLinkedSonItem(son)) continue
      if (!isPreferredRbEncimeraSon(son as any)) {
        needsPreferredAssets = true
        break
      }
    }
    if (needsPreferredAssets) break
  }

  const assets = needsPreferredAssets
    ? await loadRbPreferredEncimeraAssets(get().sceneGLB)
    : null

  if (needsPreferredAssets && !assets) {
    // console.warn(
      // `[migrateLegacyRbEncimera] No se pudo cargar ${RB_REGLETA_ENCIMERA_CODIGO}`,
    // )
  }

  let migrated = 0
  let heightFixed = 0

  for (const parentId of parentIds) {
    const parent = get().items.get(parentId as AnyID) as LinkedItem | undefined
    if (!parent?.childs) continue

    let encimeraId: string | null = null
    let encimeraSon: any = null
    for (const cid of parent.childs) {
      const son = get().items.get(cid as AnyID)
      if (son && isEncimeraLinkedSonItem(son)) {
        encimeraId = String(cid)
        encimeraSon = son
        break
      }
    }
    if (!encimeraId || !encimeraSon) continue

    const alreadyPreferred = isPreferredRbEncimeraSon(encimeraSon)
    if (!alreadyPreferred && assets) {
      const { encBack, encGlb } = assets
      let encExtra: { zonas: { idAcabadoUnidad: number; zona: string }[] } = { zonas: [] }
      if (encBack?.idUnidades) {
        try {
          const concha = await API().api.acabadosGetUnidadAcabadosInfo(encBack.idUnidades)
          if (concha && Array.isArray(concha.data?.zonas)) {
            encExtra.zonas = concha.data.zonas as {
              idAcabadoUnidad: number
              zona: string
            }[]
          }
        } catch {
          /* zonas opcionales */
        }
      }

      const oldTextures = Array.isArray(encimeraSon.textures) ? [...encimeraSon.textures] : []
      const backendY = resolveRbEncimeraBackendPosicionY(parent, get().sceneGLB)
      const option = Number(encimeraSon.option ?? 1)
      const parentOption = Number(encimeraSon.parentOption ?? 0)
      const visualOrder = encimeraSon.visualOrder ?? null

      let replacement: any
      try {
        replacement = newGetDataFromGLB({
          id: encimeraId,
          glbData: encGlb,
          type: 'linkedSon',
          catalogID: encBack.codigo ?? RB_REGLETA_ENCIMERA_CODIGO,
          firstParentID: parentId,
          visible: true,
          backGLBInfo: encBack,
          depth: 1,
          extraInfo: encExtra,
          itemInfo: {
            x: Number(encimeraSon.x ?? 0),
            y: Number(encimeraSon.y ?? 0),
            z: Number(encimeraSon.z ?? 0),
            parentOption,
            option,
            visualOrder,
            typeOfSon: encBack.descripcion ?? RB_REGLETA_ENCIMERA_CODIGO,
            description: encBack.descripcion ?? 'Encimera',
            handPosition: encimeraSon.handPosition ?? 'Normal',
            matrix: encimeraSon.matrix ?? new Matrix4().setPosition(0, 0, 0),
          },
          canCollide: false,
        }) as any
      } catch (e) {
        // console.warn(
          // '[migrateLegacyRbEncimera] Error creando B80new para',
          // parent.catalogID,
          // e,
        // )
        continue
      }

      replacement.increment = encimeraSon.increment ?? '0'
      replacement.code = encBack.codigo ?? RB_REGLETA_ENCIMERA_CODIGO
      replacement.referencia = encBack.codigo ?? RB_REGLETA_ENCIMERA_CODIGO
      replacement.referenciaBlender = encBack.referenciaBlender ?? replacement.referenciaBlender
      replacement.isUnlinkable = encimeraSon.isUnlinkable ?? false
      replacement.selected = Boolean(encimeraSon.selected)

      if (oldTextures.length > 0 && Array.isArray(replacement.textures)) {
        replacement.textures = replacement.textures.map((glbTex: any) => {
          const user =
            oldTextures.find(
              (t: any) =>
                (t.zoneDisplayName &&
                  glbTex.zoneDisplayName &&
                  String(t.zoneDisplayName).toLowerCase() ===
                    String(glbTex.zoneDisplayName).toLowerCase()) ||
                (t.zone &&
                  glbTex.zone &&
                  String(t.zone).toLowerCase() === String(glbTex.zone).toLowerCase()),
            ) ?? oldTextures[0]
          return user ? mergeGlbZoneTextureWithUserApplied(glbTex, user) : glbTex
        })
        syncLinkedItemZonesFromTextures(replacement, {
          itemId: encimeraId,
          catalogID: String(replacement.catalogID ?? ''),
          source: 'projectLoad:migrateRbEncimeraB80new',
        })
      }

      set((store) => {
        const draft = store.items.get(parentId as AnyID) as LinkedItem | undefined
        if (!draft) return
        restoreRbParentFurnitureTopHeight(draft as any, store.sceneGLB)
        placeLinkedEncimeraY(replacement, draft, {
          backendPosicionY: backendY,
        })
        store.items.set(encimeraId as AnyID, replacement)
        const payload = { glb: encGlb, backGLBInfo: encBack }
        store.sceneGLB.set(RB_REGLETA_ENCIMERA_CODIGO, payload)
        if (encBack.codigo) store.sceneGLB.set(String(encBack.codigo), payload)
        if (encBack.idUnidades != null) store.sceneGLB.set(encBack.idUnidades, payload)
      })
      migrated += 1
      heightFixed += 1
      continue
    }

    set((store) => {
      const draft = store.items.get(parentId as AnyID) as LinkedItem | undefined
      const enc = store.items.get(encimeraId as AnyID) as any
      if (!draft || !enc) return
      if (restoreRbParentFurnitureTopHeight(draft as any, store.sceneGLB)) {
        heightFixed += 1
      }
      const backendY = resolveRbEncimeraBackendPosicionY(draft, store.sceneGLB)
      placeLinkedEncimeraY(enc, draft, { backendPosicionY: backendY })
    })
  }

  if (migrated > 0 || heightFixed > 0) {
    // console.info(
    //   `[migrateLegacyRbEncimera] B10→B80new: ${migrated}; alto RB restaurado: ${heightFixed}`,
    // )
  }

  return migrated + heightFixed
}

/**
 * Contrato al abrir proyectos de catálogo antiguo con assets nuevos:
 *
 * 1. Medidas del padre = JSON de escena (width/thickness/height/y). Excepción
 *    puntual: `UNIDADES_DIMS_JSON_OVERRIDE_EXCEPTIONS` (p.ej. 2111 + KAB5060/KCB3602)
 *    → medidas desde `backResponse.unidades`.
 * 2. Catálogo/GLB actual → baselines de escala (`freeDimensionCatalogMm`,
 *    `encimeraCatalogMm`, `glbSize`) y morphs. Scale = layoutJSON / baseline.
 * 3. Encimera / hijos de huella: ancho y fondo se reajustan al padre
 *    (+ voladizo de fondo si aplica), nunca al footprint “de fábrica” del GLB nuevo.
 */
export function normalizeLegacyFreeDimensionEncimerasOnLoad({
  get,
  set,
  onlyParentIds,
}: MigrateDeps): number {
  const onlySet = onlyParentIds ? new Set(onlyParentIds) : null
  let fixed = 0

  const parentIds: string[] = []
  get().items.forEach((item, id) => {
    if (!item || item.type !== 'linkedItem') return
    if (onlySet && !onlySet.has(String(id))) return
    if (!item.childs?.size) return
    // Free-dim y kits clásicos (KBB…): ambos deben re-huellar la encimera al padre.
    parentIds.push(String(id))
  })

  for (const parentId of parentIds) {
    set((store) => {
      const parent = store.items.get(parentId as AnyID) as LinkedItem | undefined
      if (!parent?.childs?.size) return

      for (const cid of parent.childs) {
        const encimera = store.items.get(cid as AnyID) as any
        if (!encimera || !isEncimeraLinkedSonItem(encimera)) continue

        const catalogKey = encimera.catalogID || encimera.idUnidades
        const sceneEntry =
          catalogKey != null ? store.sceneGLB.get(catalogKey) : undefined
        const back = sceneEntry?.backGLBInfo as
          | { ancho?: number; fondo?: number; alto?: number; referenciaBlender?: string }
          | undefined
        const glbRoot = sceneEntry?.glb?.scene ?? sceneEntry?.glb

        if (glbRoot) {
          const glbSize = getGLBSize(glbRoot)
          if (glbSize?.x > 0 && glbSize?.z > 0) {
            encimera.glbSize = glbSize
          }
        }

        // Baseline = tamaño de fábrica del asset NUEVO (API/GLB), no el width guardado.
        const encX =
          typeof back?.ancho === 'number' && back.ancho > 0
            ? back.ancho
            : getGLBSize(glbRoot)?.x > 0
              ? Math.round(getGLBSize(glbRoot).x * 1000)
              : Number(encimera.encimeraCatalogMm?.x ?? 0)
        const encZ =
          typeof back?.fondo === 'number' && back.fondo > 0
            ? back.fondo
            : getGLBSize(glbRoot)?.z > 0
              ? Math.round(getGLBSize(glbRoot).z * 1000)
              : Number(encimera.encimeraCatalogMm?.z ?? 0)
        const encY =
          typeof back?.alto === 'number' && back.alto > 0
            ? back.alto
            : getGLBSize(glbRoot)?.y > 0
              ? Math.round(getGLBSize(glbRoot).y * 1000)
              : Number(encimera.encimeraCatalogMm?.y ?? encimera.height ?? 0)

        if (encX > 0 && encZ > 0) {
          encimera.encimeraCatalogMm = { x: encX, y: encY, z: encZ }
        }

        if (back?.referenciaBlender) {
          encimera.glbPath = back.referenciaBlender
        }

        if (Array.isArray(encimera.morphs) && encimera.morphs.length > 0) {
          encimera.morphs = activateSyntheticEncimeraFootprintMorphs(
            encimera.morphs as Morphs[],
          )
          for (const m of encimera.morphs as Morphs[]) {
            if (m.name === 'Ancho' && encX > 0) {
              m._default = encX
              if (!morphRangeOpen(m)) {
                m.min = 0
                m.max = encX
              }
              m.default = Number(m.max ?? encX)
            }
            if (m.name === 'Fondo' && encZ > 0) {
              m._default = encZ
              if (!morphRangeOpen(m)) {
                m.min = 0
                m.max = encZ
              }
              m.default = Number(m.max ?? encZ)
            }
          }
        }

        // Layout final = padre guardado (width/thickness), no el 800×620 del GLB.
        placeLinkedEncimeraY(encimera, parent)
        fixed += 1
      }
    })
  }

  return fixed
}

/**
 * Tras updateLinkedSonPositions / laterales: vuelve a anclar huella de encimera
 * al padre (el sync de hijos puede haber dejado morphs desfasados).
 */
function resyncLinkedEncimerasToSavedParentLayoutOnLoad(
  { get, set, onlyParentIds }: MigrateDeps,
  parentIdsHint?: string[],
): number {
  const onlySet = onlyParentIds ? new Set([...onlyParentIds].map(String)) : null
  const parentIds =
    parentIdsHint ??
    (() => {
      const ids: string[] = []
      get().items.forEach((item, id) => {
        if (!item || item.type !== 'linkedItem' || !item.childs?.size) return
        if (onlySet && !onlySet.has(String(id))) return
        ids.push(String(id))
      })
      return ids
    })()

  let fixed = 0
  set((store) => {
    for (const parentId of parentIds) {
      const parent = store.items.get(parentId as AnyID) as LinkedItem | undefined
      if (!parent?.childs?.size) continue
      for (const cid of parent.childs) {
        const encimera = store.items.get(cid as AnyID) as any
        if (!encimera || !isEncimeraLinkedSonItem(encimera)) continue
        placeLinkedEncimeraY(encimera, parent)
        fixed += 1
      }
    }
  })
  return fixed
}

function restoreLinkedApplianceApiFloorCoordsOnLoad({ get, set, onlyParentIds }: MigrateDeps): number {
  const onlySet = onlyParentIds ? new Set([...onlyParentIds].map(String)) : null
  let fixed = 0

  set((store) => {
    store.items.forEach((parent, parentId) => {
      if (!parent || parent.type !== 'linkedItem') return
      if (onlySet && !onlySet.has(String(parentId))) return
      if (!usesFreeDimensionScaling(parent) || !parent.childs?.size) return

      const sceneEntry =
        store.sceneGLB.get(parent.catalogID) ||
        store.sceneGLB.get(String(parent.catalogID ?? '')) ||
        (parent.idUnidades != null ? store.sceneGLB.get(parent.idUnidades) : undefined)
      const back = sceneEntry?.backGLBInfo as
        | {
            elementosEnlazados?: Array<{
              idElementosEnlazados?: number
              descripcion?: string | null
              nodo?: number | null
              posicionX?: number | null
              posicionY?: number | null
              posicionZ?: number | null
            }>
          }
        | undefined
      const elementos =
        back?.elementosEnlazados ??
        (back as { elementosEnlazadosAcabadosPorDefecto?: typeof back.elementosEnlazados })
          ?.elementosEnlazadosAcabadosPorDefecto ??
        []

      // Encimera hermana: el ajuste Y de placa/fregadero/grifo = grosor − 10 mm (baseline API).
      let encimeraSibling: any = null
      for (const cid of parent.childs) {
        const son = store.items.get(cid as AnyID)
        if (son && isEncimeraLinkedSonItem(son)) {
          encimeraSibling = son
          break
        }
      }
      const encimeraAjuste = resolveEncimeraAcabadoAjusteYMm(encimeraSibling)

      for (const cid of parent.childs) {
        const child = store.items.get(cid as AnyID)
        if (!child || child.type !== 'linkedSon') continue
        if (!linkedSonKeepsApiLocalTransform(child)) continue
        if (isIndependentZocaloLinkedSon(child) || isEncimeraLinkedSonItem(child)) continue

        ;(child as any).encimeraAcabadoAjusteY = encimeraAjuste

        const sealed = sealLinkedApplianceApiFloorCoordsFromParentCatalog(
          child as any,
          parent as any,
          elementos,
        )
        if (sealed) {
          applyLinkedApplianceLocalTransformFromApi(child as any, parent as any)
          fixed += 1
        }
      }
    })
  })

  return fixed
}

function resyncLinkedPlacasToEncimeraOnLoad({ set }: MigrateDeps, parentIds: string[]) {
  set((store) => {
    for (const parentId of parentIds) {
      const parent = store.items.get(parentId as AnyID) as LinkedItem | undefined
      if (!parent?.childs?.size) continue

      let encimera: {
        textures?: { acabado?: string | null }[] | null
        height?: number | null
        catalogID?: string | number
        idUnidades?: number
        glbSize?: { y?: number }
        morphs?: unknown
        encimeraCatalogMm?: { y?: number }
      } | null = null
      for (const cid of parent.childs) {
        const son = store.items.get(cid as AnyID)
        if (son && isEncimeraLinkedSonItem(son)) {
          encimera = son
          break
        }
      }
      const ajuste = resolveEncimeraAcabadoAjusteYMm(encimera)
      const encimeraAabb = encimera
        ? getCatalogGlbAabbMm(store.sceneGLB as never, encimera.catalogID, [
            sceneGLBLookupKey(String(encimera.catalogID ?? '')),
            encimera.idUnidades,
          ])
        : null

      for (const cid of parent.childs) {
        const child = store.items.get(cid as AnyID)
        if (!child || child.type !== 'linkedSon') continue
        if (!isPlacaLinkedApplianceSon(child)) continue
        ;(child as { encimeraAcabadoAjusteY?: number }).encimeraAcabadoAjusteY = ajuste
        const catalogKey = String((child as { catalogID?: string }).catalogID ?? '')
        const glbEntry =
          store.sceneGLB.get(catalogKey) ||
          store.sceneGLB.get((child as { catalogID?: string }).catalogID as never)
        const glbRoot = glbEntry?.glb?.scene ?? glbEntry?.glb
        const aabb = getObjectLocalAabbMm(glbRoot)
        if (aabb) {
          const plateW = aabb.maxX - aabb.minX
          ;(child as { width?: number }).width = plateW
          ;(child as { height?: number }).height = aabb.maxY - aabb.minY
          ;(child as { thickness?: number }).thickness = aabb.maxZ - aabb.minZ
          const centered = getLinkedPlacaCenteredLocalXMm(
            parent.width ?? 0,
            plateW,
            aabb.minX,
          )
          if (centered != null) {
            child.x = centered
            ;(child as { posicionX?: number }).posicionX = centered
          }
        }
        applyLinkedApplianceLocalTransformFromApi(child as never, parent as never, encimera as never, {
          glbMinYMm: aabb?.minY ?? 0,
          encimeraAabb,
        })
      }
    }
  })
}

function syncSonsAndLateralsAfterHydrate({ get }: MigrateDeps, parentIds: string[]) {
  for (const parentId of parentIds) {
    const parent = get().items.get(parentId as AnyID) as LinkedItem | undefined
    if (!parent) continue

    if (parent.childs?.size) {
      try {
        get().updateLinkedSonPositions?.(parentId, 'Ancho')
        get().updateLinkedSonPositions?.(parentId, 'Fondo')
        get().updateLinkedSonPositions?.(parentId, 'Alto')
        if (
          isKbrRinconeroFamilyCatalog(parent.catalogID) ||
          isKbrRinconeroFamilyCatalog(parent.codigo) ||
          isKbrRinconeroFamilyCatalog(parent.referencia)
        ) {
          get().updateLinkedSonPositions?.(parentId, 'Regleta')
        }
      } catch (e) {
        // console.warn('[hydrateLegacy] updateLinkedSonPositions', parentId, e)
      }
    }

    // Laterales: misma ruta que editMorph Alto/Fondo/Ancho en proyecto nuevo.
    if (
      !isFreeDimensionLateralOrCostadoCatalog(parent) &&
      typeof get().syncLateralsAfterFurnitureDimensionChange === 'function'
    ) {
      const tipo = String(parent.tipoNormalizado ?? '').toUpperCase()
      if (tipo === 'MBJ' || tipo === 'COL' || tipo === 'MAL') {
        try {
          get().syncLateralsAfterFurnitureDimensionChange?.(parentId as AnyID)
        } catch (e) {
          // console.warn('[hydrateLegacy] syncLaterals', parentId, e)
        }
      }
    }
  }
}

/**
 * Entry point: deja un proyecto antiguo en el mismo estado funcional que drawItem.
 * Tras guardar, el JSON ya lleva zócalo 4000 / free-dim / glbPath fresco —
 * no hace falta “arrastrar” el catálogo viejo en aperturas siguientes.
 *
 * @returns true si hubo cambios de contrato (convienen guardar).
 */
/**
 * Quita zócalo hijo mal añadido a regletas RB* (bajos). No toca RB805.
 * RC* (p.ej. RC2305) sí lleva tira Zocalo 4000 al ancho de la regleta.
 */
export function stripIndependentZocaloFromKbrParents({
  get,
  set,
  onlyParentIds,
}: MigrateDeps): number {
  const onlySet = onlyParentIds ? new Set([...onlyParentIds].map(String)) : null
  const parentIds: string[] = []
  get().items.forEach((item, id) => {
    if (!item || item.type !== 'linkedItem') return
    if (onlySet && !onlySet.has(String(id))) return
    // Solo RB*. RC* debe conservar / recibir Zocalo 4000.
    if (!String(item.catalogID ?? item.codigo ?? '').toUpperCase().startsWith('RB')) return
    if (isRb805Catalog(item)) return
    if (!isRbFreeDimensionRegleta(item)) return
    const hasFlag = item.hasIndependentZocalo === true
    const hasSon =
      typeof item.independentZocaloSonId === 'string' && item.independentZocaloSonId.length > 0
    if (hasFlag || hasSon) parentIds.push(String(id))
  })
  if (parentIds.length === 0) return 0

  let stripped = 0
  set((store) => {
    for (const parentId of parentIds) {
      const parent = store.items.get(parentId as AnyID) as LinkedItem & Record<string, any> | undefined
      if (!parent) continue
      if (!String(parent.catalogID ?? parent.codigo ?? '').toUpperCase().startsWith('RB')) continue
      if (isRb805Catalog(parent)) continue

      const sonIds = new Set<string>()
      if (typeof parent.independentZocaloSonId === 'string' && parent.independentZocaloSonId) {
        sonIds.add(parent.independentZocaloSonId)
      }
      if (parent.childs?.size) {
        for (const cid of parent.childs) {
          const child = store.items.get(cid as AnyID)
          if (child && isIndependentZocaloLinkedSon(child)) sonIds.add(String(cid))
        }
      }

      for (const sonId of sonIds) {
        store.items.delete(sonId as AnyID)
        parent.childs?.delete(sonId as AnyID)
        if (Array.isArray(parent.childrenOrder)) {
          parent.childrenOrder = parent.childrenOrder.filter(
            (k) => !String(k).includes(sonId),
          )
        }
      }

      revertIndependentZocaloFloorOffsetIfNeeded(parent)
      parent.hasIndependentZocalo = false
      parent.independentZocaloSonId = undefined
      parent.independentZocaloHeightMm = undefined
      stripped += 1
    }
  })

  if (stripped > 0) {
    // console.info(
    //   `[stripIndependentZocalo] Quitado zócalo erróneo de ${stripped} regleta(s) RB`,
    // )
  }
  return stripped
}

/** Ancho/fondo (y alto MAL) de planta del JSON antes de hidratar con catálogo nuevo. */
function snapshotSavedFootprintLayoutMm(
  get: MigrateDeps['get'],
  onlyParentIds?: Iterable<string>,
): Map<string, { width: number; thickness: number; height: number }> {
  const onlySet = onlyParentIds ? new Set([...onlyParentIds].map(String)) : null
  const snap = new Map<string, { width: number; thickness: number; height: number }>()
  get().items.forEach((item, id) => {
    if (!item || (item.type !== 'linkedItem' && item.type !== 'items')) return
    if (onlySet && !onlySet.has(String(id))) return
    const width = Number(item.width ?? 0)
    const thickness = Number(item.thickness ?? 0)
    const height = Number(item.height ?? 0)
    if (width > 0 || thickness > 0 || height > 0) {
      snap.set(String(id), { width, thickness, height })
    }
  })
  return snap
}

/**
 * Reafirma ancho/fondo (y alto MAL) del JSON tras hidratar.
 * Alto de bajas NO se reafirma a ciegas: legacy 900 → cuerpo 800 + zócalo 100.
 */
function restoreSavedFootprintLayoutMm(
  { set }: MigrateDeps,
  snap: Map<string, { width: number; thickness: number; height: number }>,
): number {
  if (snap.size === 0) return 0
  const STALE_LO = 0.72
  const STALE_HI = 1.38
  let restored = 0
  set((store) => {
    for (const [id, dims] of snap) {
      const item = store.items.get(id as AnyID) as LinkedItem | undefined
      if (!item) continue
      let changed = false
      if (dims.width > 0 && Number(item.width ?? 0) !== dims.width) {
        item.width = dims.width
        changed = true
      }
      if (dims.thickness > 0 && Number(item.thickness ?? 0) !== dims.thickness) {
        const isRaColumn = /^RA\d/i.test(String(item.catalogID ?? item.codigo ?? ''))
        const glbZ = Number(item.freeDimensionGlbBaseMm?.z ?? 0)
        const liveT = Number(item.thickness ?? 0)
        // No deshacer corrección RA* (JSON 600 → mesh 350).
        const raFondoAlreadyFixed =
          isRaColumn && glbZ > 0 && Math.abs(liveT - glbZ) <= 2
        if (!raFondoAlreadyFixed) {
          item.thickness = dims.thickness
          changed = true
        }
      }
      // MAL: alto de la unidad / layout guardado (sin zócalo). No restaurar ratios extremos
      // (KAB legacy corrupto 400 vs catálogo 800 → dejar reconcile).
      // Frontal/TPC: restaurar siempre el alto guardado (50→100+ es normal en tiras).
      if (
        (isMalFreeDimensionUnit(item) || isFreeDimensionTpcNoMorphCatalog(item)) &&
        dims.height > 0
      ) {
        const catY = Number(item.freeDimensionCatalogMm?.y ?? 0)
        const ratio = catY > 0 ? dims.height / catY : 1
        const legitimate =
          isFreeDimensionTpcNoMorphCatalog(item) ||
          catY <= 0 ||
          (ratio >= STALE_LO && ratio <= STALE_HI)
        if (legitimate && Number(item.height ?? 0) !== dims.height) {
          item.height = dims.height
          changed = true
        }
      }
      if (!changed) continue
      restored += 1
      if (Array.isArray(item.morphs)) {
        for (const m of item.morphs as Morphs[]) {
          if (m.name === 'Ancho' && Number(item.width) > 0) m.default = Number(item.width)
          if (m.name === 'Fondo' && Number(item.thickness) > 0) m.default = Number(item.thickness)
          if (m.name === 'Alto' && Number(item.height) > 0) m.default = Number(item.height)
        }
      }
    }
  })
  if (restored > 0) {
    // console.info(
    //   `[restoreSavedFootprint] Restaurados ancho/fondo(/alto MAL|TPC) guardados en ${restored} unidad(es)`,
    // )
  }
  return restored
}

export async function hydrateLegacyUnitsToDrawItemParity(
  deps: MigrateDeps,
): Promise<boolean> {
  const existingCost =
    typeof window !== 'undefined'
      ? (window as Window & { __sceneCost?: { status?: string; phases?: unknown } }).__sceneCost
      : undefined
  const sessionFromOpen = Boolean(existingCost && (existingCost.status === 'running' || existingCost.phases))
  if (!sessionFromOpen) {
    beginSceneCostSession()
    markSceneCostPhase('hydrate')
  }

  const kbrZocalosRemoved = stripIndependentZocaloFromKbrParents(deps)

  // Excepción puntual (p.ej. 2111+KAB5060): JSON incorrecto → medidas desde unidades.
  // Resto: JSON de escena.
  const unidadesOpts = {
    idVersiones: deps.idVersiones,
    skipHeightIfIndependentZocalo: true as const,
  }
  let unidadesApplied = 0
  if (deps.projectUnidades?.length) {
    deps.set((store) => {
      unidadesApplied = applyProjectUnidadesDimensionsToItems(
        store.items,
        deps.projectUnidades,
        deps.onlyParentIds,
        { idVersiones: deps.idVersiones },
      )
    })
  }

  const beforeIds = collectFreeDimParentIds(deps.get().items, deps.onlyParentIds)
  // Snapshot de planta (JSON, o excepción ya corregida) para reafirmar tras hydrate.
  const savedFootprint = snapshotSavedFootprintLayoutMm(deps.get, deps.onlyParentIds)

  // ¿Algún padre free-dim aún sin contrato nuevo?
  let needsUpgrade = false
  for (const id of beforeIds) {
    const item = deps.get().items.get(id as AnyID) as LinkedItem | undefined
    if (!item) continue
    if (shouldUseIndependentZocaloUnit(item) && !item.hasIndependentZocalo) {
      needsUpgrade = true
      break
    }
    if (!item.freeDimensionCatalogMm?.x || !item.freeDimensionGlbBaseMm?.y) {
      needsUpgrade = true
      break
    }
    if (!Array.isArray(item.morphs) || item.morphs.length === 0) {
      needsUpgrade = true
      break
    }
  }

  hydrateFreeDimensionItemsFromScene(deps)
  const zocalosAdded = await migrateLegacyIndependentZocalosOnLoad(deps)
  // Re-hidratar padres tras zócalo (hasIndependentZocalo cambia lift/scale Y).
  hydrateFreeDimensionItemsFromScene(deps)
  // JSON antiguo: regleta KBR +20 mm (60→80) en store + morph sidebar.
  const kbrRegletaBumped = applyKbrLegacyRegletaPlus20OnLoad(deps)
  // RB antiguos: Encimera B10 → B80new + alto store 900 (mismo contrato que drawItem).
  const rbEncimerasMigrated = await migrateLegacyRbEncimeraB10ToB80newOnLoad(deps)

  // Reafirmar footprint del JSON (o excepción) antes de huellar encimeras/hijos.
  // Alto: bajas 900 ya convertidas a cuerpo 800 (+ zócalo 100); RB a tope 900.
  const footprintRestored = restoreSavedFootprintLayoutMm(deps, savedFootprint)

  // Reimponer solo excepciones (2111+KAB5060) tras reconcile; no pisa el JSON normal.
  if (deps.projectUnidades?.length) {
    deps.set((store) => {
      unidadesApplied += applyProjectUnidadesDimensionsToItems(
        store.items,
        deps.projectUnidades,
        deps.onlyParentIds,
        unidadesOpts,
      )
    })
    await migrateLegacyRbEncimeraB10ToB80newOnLoad(deps)
  }

  const encimerasFixed = normalizeLegacyFreeDimensionEncimerasOnLoad(deps)

  // Quitar restos de zócalo embebido en texturas del padre (viven en el hijo).
  stripObsoleteEmbeddedZocaloTexturesFromParents(deps)

  // Placa/fregadero/grifo: recuperar posicionY API (nunca el y local corrupto del JSON).
  const appliancesFixed = restoreLinkedApplianceApiFloorCoordsOnLoad(deps)

  const parentIds = collectFreeDimParentIds(deps.get().items, deps.onlyParentIds)
  syncSonsAndLateralsAfterHydrate(deps, parentIds)

  // syncLaterals puede haber pisado CC24060; reimponer solo excepciones (no todo el JSON).
  if (deps.projectUnidades?.length) {
    deps.set((store) => {
      unidadesApplied += applyProjectUnidadesDimensionsToItems(
        store.items,
        deps.projectUnidades,
        deps.onlyParentIds,
        unidadesOpts,
      )
    })
  }

  // Tras sync (y posibles laterales): re-anclar zócalo al width vivo del padre (551, etc.).
  resyncIndependentZocalosToParentWidth(deps, parentIds)
  // Encimeras otra vez al ancho/fondo del padre (por si updateLinkedSonPositions las movió).
  resyncLinkedEncimerasToSavedParentLayoutOnLoad(deps, parentIds)

  // Encimera ya en Y final (900, no 800): resellar placa y anclar al grosor del acabado.
  restoreLinkedApplianceApiFloorCoordsOnLoad(deps)
  resyncLinkedPlacasToEncimeraOnLoad(deps, parentIds)

  const upgraded =
    needsUpgrade ||
    unidadesApplied > 0 ||
    zocalosAdded > 0 ||
    rbEncimerasMigrated > 0 ||
    encimerasFixed > 0 ||
    appliancesFixed > 0 ||
    kbrZocalosRemoved > 0 ||
    footprintRestored > 0 ||
    kbrRegletaBumped > 0
  // console.info(
  //   `[hydrateLegacyUnitsToDrawItemParity] listo (${parentIds.length} free-dim)` +
  //     (unidadesApplied > 0 ? ` — ${unidadesApplied} desde unidades` : '') +
  //     (upgraded ? ' — contrato actualizado; guardar para persistir catálogo nuevo' : ''),
  // )
  const published =
    typeof window !== 'undefined'
      ? (window as Window & { __sceneCost?: unknown }).__sceneCost
      : undefined
  // console.log('[SceneCost] probe tras hydrate', published ?? 'AUSENTE — recarga dura (Cmd+Shift+R)')
  if (!sessionFromOpen) {
    endSceneCostSession()
  }
  return upgraded
}

/** Zócalo hijo: width/scaleX = padre tras migrar morphs/laterales. */
function resyncIndependentZocalosToParentWidth({ get, set }: MigrateDeps, parentIds: string[]) {
  set((store) => {
    for (const parentId of parentIds) {
      const parent = store.items.get(parentId as AnyID) as LinkedItem | undefined
      if (!parent?.hasIndependentZocalo || !parent.childs?.size) continue
      for (const cid of parent.childs) {
        const son = store.items.get(cid as AnyID)
        if (!son || !isIndependentZocaloLinkedSon(son)) continue
        const cached =
          store.sceneGLB.get(String(son.catalogID)) ||
          (son.idUnidades != null ? store.sceneGLB.get(son.idUnidades) : undefined)
        const glbRoot = cached?.glb?.scene ?? cached?.glb
        placeLinkedIndependentZocalo(son as any, parent, {
          glbSizeMm: glbRoot
            ? vector3ToFreeDimensionGlbBaseMm(getGLBSize(glbRoot))
            : undefined,
          backGLBInfo: cached?.backGLBInfo as
            | { ancho?: number; alto?: number; fondo?: number }
            | undefined,
          items: store.items,
        })
      }
    }
  })
}

/** El acabado de zócalo ya no va en el padre (GLB sin losa); limpiar zonas/texturas huérfanas. */
function stripObsoleteEmbeddedZocaloTexturesFromParents({ get, set, onlyParentIds }: MigrateDeps) {
  const onlySet = onlyParentIds ? new Set(onlyParentIds) : null
  set((store) => {
    store.items.forEach((item, id) => {
      if (!item || item.type !== 'linkedItem') return
      if (onlySet && !onlySet.has(String(id))) return
      if (!item.hasIndependentZocalo) return

      if (Array.isArray(item.textures) && item.textures.length > 0) {
        const next = item.textures.filter((t: any) => {
          const zone = String(t?.zoneDisplayName ?? t?.zone ?? t?.zoneName ?? '')
          return !/z[oó]calo/i.test(zone)
        })
        if (next.length !== item.textures.length) {
          item.textures = next
        }
      }

      if (item.zones && typeof item.zones === 'object') {
        for (const key of Object.keys(item.zones)) {
          if (/z[oó]calo/i.test(key)) {
            delete item.zones[key]
          }
        }
      }
    })
  })
}
